import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WellnessHistory = ({ entries, onEdit, onDeleteAll, onImport, onExport, className = "" }) => {
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);

  const getHooperColor = (score) => {
    if (score <= 12) return 'text-success bg-success/10';
    if (score <= 16) return 'text-accent bg-accent/10';
    if (score <= 20) return 'text-warning bg-warning/10';
    return 'text-error bg-error/10';
  };

  const getHooperStatus = (score) => {
    if (score <= 12) return 'Excellent';
    if (score <= 16) return 'Good';
    if (score <= 20) return 'Fair';
    return 'Poor';
  };

  const getTrendIcon = (current, previous) => {
    if (!previous) return null;
    if (current < previous) return { icon: 'TrendingDown', color: 'text-success' };
    if (current > previous) return { icon: 'TrendingUp', color: 'text-error' };
    return { icon: 'Minus', color: 'text-text-secondary' };
  };

  const sortedEntries = [...entries]?.sort((a, b) => {
    let aVal = a?.[sortBy];
    let bVal = b?.[sortBy];
    
    if (sortBy === 'date') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className={`bg-card rounded-lg border border-border ${className}`}>
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">Wellness History</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-text-secondary">
              {entries?.length} entries
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={onImport}
              iconName="Upload"
              iconPosition="left"
            >
              Import CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              iconName="Download"
              iconPosition="left"
            >
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteAllConfirm(true)}
              iconName="Trash2"
              iconPosition="left"
              disabled={entries?.length === 0}
              className="text-error hover:text-error hover:border-error"
            >
              Delete All
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('date')}
                  className="flex items-center space-x-1 text-sm font-medium text-text-primary hover:text-accent"
                >
                  <span>Date</span>
                  <Icon 
                    name={sortBy === 'date' && sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('hooperIndex')}
                  className="flex items-center space-x-1 text-sm font-medium text-text-primary hover:text-accent"
                >
                  <span>Hooper Index</span>
                  <Icon 
                    name={sortBy === 'hooperIndex' && sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-center p-4 text-sm font-medium text-text-primary">Sleep</th>
              <th className="text-center p-4 text-sm font-medium text-text-primary">Fatigue</th>
              <th className="text-center p-4 text-sm font-medium text-text-primary">Soreness</th>
              <th className="text-center p-4 text-sm font-medium text-text-primary">Stress</th>
              <th className="text-center p-4 text-sm font-medium text-text-primary">Trend</th>
              <th className="text-center p-4 text-sm font-medium text-text-primary">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedEntries?.map((entry, index) => {
              const previousEntry = index < sortedEntries?.length - 1 ? sortedEntries?.[index + 1] : null;
              const trend = getTrendIcon(entry?.hooperIndex, previousEntry?.hooperIndex);
              
              return (
                <tr key={entry?.id} className="border-b border-border hover:bg-muted/50">
                  <td className="p-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-text-primary">
                        {new Date(entry.date)?.toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                      <span className="text-xs text-text-secondary">
                        {new Date(entry.date)?.toLocaleDateString('en-GB', {
                          weekday: 'short'
                        })}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-sm font-semibold ${getHooperColor(entry?.hooperIndex)}`}>
                          {entry?.hooperIndex}
                        </span>
                        <span className={`text-sm font-medium ${getHooperColor(entry?.hooperIndex)?.replace('bg-', 'text-')?.replace('/10', '')}`}>
                          {getHooperStatus(entry?.hooperIndex)}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-sm font-medium text-text-primary">{entry?.sleep}</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-sm font-medium text-text-primary">{entry?.fatigue}</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-sm font-medium text-text-primary">{entry?.soreness}</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-sm font-medium text-text-primary">{entry?.stress}</span>
                  </td>
                  <td className="p-4 text-center">
                    {trend && (
                      <Icon 
                        name={trend?.icon} 
                        size={16} 
                        className={trend?.color}
                        strokeWidth={2}
                      />
                    )}
                  </td>
                  <td className="p-4 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(entry)}
                      iconName="Edit2"
                      className="h-8 w-8 p-0"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {entries?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Calendar" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <h4 className="text-lg font-medium text-text-primary mb-2">No wellness entries yet</h4>
          <p className="text-text-secondary">Start tracking your daily wellness to see your history here.</p>
        </div>
      )}

      {/* Delete All Confirmation Dialog */}
      {showDeleteAllConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                  <Icon name="AlertTriangle" size={24} color="var(--color-error)" strokeWidth={2} />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-text-primary mb-2">Delete All Wellness Entries?</h3>
                <p className="text-sm text-text-secondary mb-4">
                  This will permanently delete all {entries?.length} wellness entries. This action cannot be undone.
                </p>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteAllConfirm(false)}
                    className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary smooth-transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowDeleteAllConfirm(false);
                      onDeleteAll();
                    }}
                    className="px-4 py-2 text-sm font-medium bg-error text-error-foreground rounded-lg hover:bg-error/90 smooth-transition"
                  >
                    Delete All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WellnessHistory;